import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
} from '@mui/material';
import { DataGrid, frFR } from '@mui/x-data-grid';
import {
  Search as SearchIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
  Visibility as VisibilityIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { subscriberService } from '../../services/api';

const Subscribers = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [totalRows, setTotalRows] = useState(0);
  const [counts, setCounts] = useState({ pending: 0, active: 0, blocked: 0, total: 0 });
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: '', subscriber: null });
  
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const statusFilter = ['', 'pending', 'active', 'blocked'][tabValue];

  const fetchSubscribers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await subscriberService.getAll({
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        search,
        status: statusFilter || undefined,
      });
      
      setSubscribers(response.data.data);
      setTotalRows(response.data.pagination.total);
      if (response.data.counts) setCounts(response.data.counts);
    } catch (error) {
      enqueueSnackbar('Erreur lors du chargement', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [paginationModel, search, statusFilter, enqueueSnackbar]);

  useEffect(() => {
    fetchSubscribers();
  }, [fetchSubscribers]);

  const handleValidate = async (subscriber) => {
    try {
      await subscriberService.validate(subscriber.id);
      enqueueSnackbar('Abonné validé avec succès', { variant: 'success' });
      fetchSubscribers();
    } catch (error) {
      enqueueSnackbar('Erreur lors de la validation', { variant: 'error' });
    }
    setConfirmDialog({ open: false, action: '', subscriber: null });
  };

  const handleBlock = async (subscriber) => {
    try {
      await subscriberService.block(subscriber.id);
      enqueueSnackbar('Abonné bloqué', { variant: 'warning' });
      fetchSubscribers();
    } catch (error) {
      enqueueSnackbar('Erreur lors du blocage', { variant: 'error' });
    }
    setConfirmDialog({ open: false, action: '', subscriber: null });
  };

  const getStatusChip = (status) => {
    const config = {
      active: { color: 'success', label: 'Actif' },
      pending: { color: 'warning', label: 'En attente' },
      inactive: { color: 'default', label: 'Inactif' },
      blocked: { color: 'error', label: 'Bloqué' },
    };
    const { color, label } = config[status] || { color: 'default', label: status };
    return <Chip label={label} color={color} size="small" />;
  };

  const columns = [
    { 
      field: 'phone_number', 
      headerName: 'Téléphone', 
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WhatsAppIcon color="success" fontSize="small" />
          {params.row.country_code}{params.value}
        </Box>
      ),
    },
    { 
      field: 'first_name', 
      headerName: 'Prénom', 
      width: 120,
    },
    { 
      field: 'last_name', 
      headerName: 'Nom', 
      width: 120,
    },
    { 
      field: 'preferredLanguage', 
      headerName: 'Langue', 
      width: 100,
      valueGetter: (params) => params.row.preferredLanguage?.name || 'Français',
    },
    { 
      field: 'status', 
      headerName: 'Statut', 
      width: 120,
      renderCell: (params) => getStatusChip(params.value),
    },
    { 
      field: 'subscription_date', 
      headerName: 'Inscription', 
      width: 130,
      valueFormatter: (params) => new Date(params.value).toLocaleDateString('fr-FR'),
    },
    { 
      field: 'total_messages_received', 
      headerName: 'Messages', 
      width: 100,
      align: 'center',
    },
    { 
      field: 'actions', 
      headerName: 'Actions', 
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Voir détails">
            <IconButton 
              size="small" 
              onClick={() => navigate(`/subscribers/${params.row.id}`)}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          {params.row.status === 'pending' && (
            <Tooltip title="Valider">
              <IconButton 
                size="small" 
                color="success"
                onClick={() => setConfirmDialog({ 
                  open: true, 
                  action: 'validate', 
                  subscriber: params.row 
                })}
              >
                <CheckCircleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          
          {params.row.status !== 'blocked' && (
            <Tooltip title="Bloquer">
              <IconButton 
                size="small" 
                color="error"
                onClick={() => setConfirmDialog({ 
                  open: true, 
                  action: 'block', 
                  subscriber: params.row 
                })}
              >
                <BlockIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          Gestion des abonnés
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchSubscribers}
        >
          Actualiser
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
            <Tab label="Tous" />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  En attente
                  <Chip label={counts.pending} size="small" color="warning" />
                </Box>
              } 
            />
            <Tab label="Actifs" />
            <Tab label="Bloqués" />
          </Tabs>
        </Box>

        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            placeholder="Rechercher par numéro, nom..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <DataGrid
            rows={subscribers}
            columns={columns}
            loading={loading}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 20, 50]}
            rowCount={totalRows}
            paginationMode="server"
            localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
            autoHeight
            disableRowSelectionOnClick
            sx={{
              '& .MuiDataGrid-cell:focus': { outline: 'none' },
              '& .MuiDataGrid-row:hover': { backgroundColor: 'action.hover' },
            }}
          />
        </Box>
      </Card>

      {/* Dialog de confirmation */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, action: '', subscriber: null })}>
        <DialogTitle>
          {confirmDialog.action === 'validate' ? 'Valider cet abonné ?' : 'Bloquer cet abonné ?'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirmDialog.action === 'validate' 
              ? `L'abonné ${confirmDialog.subscriber?.phone_number} pourra recevoir les éphémérides quotidiens.`
              : `L'abonné ${confirmDialog.subscriber?.phone_number} ne recevra plus les éphémérides.`
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, action: '', subscriber: null })}>
            Annuler
          </Button>
          <Button 
            variant="contained"
            color={confirmDialog.action === 'validate' ? 'success' : 'error'}
            onClick={() => confirmDialog.action === 'validate' 
              ? handleValidate(confirmDialog.subscriber)
              : handleBlock(confirmDialog.subscriber)
            }
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Subscribers;
